import { useMemo } from 'react';

import dayjs, { type Dayjs } from 'dayjs';

import type {
  GetClientRecordsListQuery,
  GetEngagementRecordsListQuery,
  GetFilteredClientRecordsQuery,
} from '@/gql/operations';
import { generateUserImageUrl } from '@/shared/helpers/userImagesUrlUtils';

type EngagementRecord = GetFilteredClientRecordsQuery['mlfsl_client'][number];
type EngagementListRecord = GetClientRecordsListQuery['mlfsl_client'][number];
type EngagementDetailRecord = GetEngagementRecordsListQuery['clm_engagement_records'][number];

// Personnel come from several query fields whose PrimaryPracticeGroup selection
// differs (ResponsibleAttorney selects SubSubPracticeGroupName, while the
// *_by_user fields select PracticeGroupName). This structural type tolerates
// either shape so parsePersonnel can accept them all.
type PersonnelInput =
  | {
      ActiveDirectoryLogin?: string | null;
      NamePreferredFull?: string | null;
      Email?: string | null;
      TitleFullyQualified?: string | null;
      PrimaryPracticeGroup?: {
        SubSubPracticeGroupName?: string | null;
        PracticeGroupName?: string | null;
      } | null;
      BusinessPrimaryOfficePhoneNumberFull?: string | null;
      BusinessPrimaryOffice?: string | null;
    }
  | null
  | undefined;

export interface ParsedPersonnel {
  adSystemId?: string;
  displayName?: string;
  email?: string;
  title?: string;
  imageUrlSmall?: string;
  practiceGroup?: string;
  phone?: string;
  office?: string;
}

export interface ParsedIManageDocument {
  id: string;
  docId: string;
  docName?: string;
  updatedAt?: Dayjs;
  updatedByUser?: ParsedPersonnel;
}

export interface ParsedImanageComment {
  id: string;
  comments: string;
  createdByUser?: ParsedPersonnel;
  updatedAt: Dayjs;
  updatedByUser?: ParsedPersonnel;
}

export interface ParsedEngagementRecord {
  id: string;
  engagementRecordId?: string;
  clientNumber: string;
  clientName?: string;
  clientOpenDate?: Dayjs | null;
  clientClosedDate?: Dayjs | null;
  isExecuted: boolean;
  type: string;
  responsibleAttorney?: ParsedPersonnel;
  practiceGroup?: string;
  iManageDocuments: ParsedIManageDocument[];
  iManageComments: ParsedImanageComment[];
  createdAt: Dayjs | null;
  createdBy: string;
  createdByUser?: ParsedPersonnel;
  updatedAt: Dayjs | null;
  updatedBy: string;
  updatedByUser?: ParsedPersonnel;
}

export function parseEngagementRecords(filteredData: EngagementRecord[]): ParsedEngagementRecord[] {
  if (!filteredData) return [];

  return filteredData.map((record) => {
    const parsePersonnel = (user: PersonnelInput): ParsedPersonnel | undefined => {
      if (!user) return undefined;

      return {
        adSystemId: user.ActiveDirectoryLogin || undefined,
        displayName: user.NamePreferredFull || undefined,
        email: user.Email || undefined,
        title: user.TitleFullyQualified || undefined,
        imageUrlSmall: user.Email ? generateUserImageUrl(user.Email, 'M') : undefined,
        practiceGroup:
          user.PrimaryPracticeGroup?.SubSubPracticeGroupName ||
          user.PrimaryPracticeGroup?.PracticeGroupName ||
          undefined,
        phone: user.BusinessPrimaryOfficePhoneNumberFull || undefined,
        office: user.BusinessPrimaryOffice || undefined,
      };
    };

    return {
      id: record.ClientNumber,
      engagementRecordId: record.cmap_clm_engagement_record?.id,
      clientNumber: record.ClientNumber,
      clientName: record.ClientName1 || undefined,
      clientOpenDate:
        record.DateClientOpen && record.DateClientOpen > -1
          ? dayjs(record.DateClientOpen.toString(), 'YYYYMMDD')
          : null,
      clientClosedDate:
        record.DateClientClosed && record.DateClientClosed > -1
          ? dayjs(record.DateClientClosed.toString(), 'YYYYMMDD')
          : null,
      responsibleAttorney: parsePersonnel(record.ResponsibleAttorney),
      practiceGroup: record.ResponsibleAttorney?.PrimaryPracticeGroup?.SubSubPracticeGroupName || undefined,
      isExecuted: Boolean(record.cmap_clm_engagement_record?.engagement_imanage_documents?.length),
      type: record.cmap_clm_engagement_record?.type || '',
      iManageDocuments: (record.cmap_clm_engagement_record?.engagement_imanage_documents ?? []).map((doc) => ({
        id: doc.id,
        docId: doc.doc_id,
        docName: doc.name,
        updatedAt: doc.updated_at ? dayjs(doc.updated_at) : undefined,
        updatedByUser: parsePersonnel(doc.updated_by_user),
      })),
      iManageComments: (record.cmap_clm_engagement_record?.engagement_client_comments ?? []).map((comment) => ({
        id: comment.id,
        comments: comment.comments,
        createdByUser: parsePersonnel(comment.created_by_user),
        updatedAt: dayjs(comment.updated_at),
        updatedByUser: parsePersonnel(comment.updated_by_user),
      })),
      createdAt: record.cmap_clm_engagement_record?.created_at
        ? dayjs(record.cmap_clm_engagement_record.created_at)
        : null,
      createdBy: record.cmap_clm_engagement_record?.created_by_user?.ActiveDirectoryLogin || '',
      createdByUser: parsePersonnel(record.cmap_clm_engagement_record?.created_by_user),
      updatedAt: record.cmap_clm_engagement_record?.updated_at
        ? dayjs(record.cmap_clm_engagement_record.updated_at)
        : null,
      updatedBy: record.cmap_clm_engagement_record?.updated_by_user?.ActiveDirectoryLogin || '',
      updatedByUser: parsePersonnel(record.cmap_clm_engagement_record?.updated_by_user),
    };
  });
}

function useParseRecords(filteredData: EngagementRecord[]): ParsedEngagementRecord[] {
  return useMemo(() => parseEngagementRecords(filteredData), [filteredData]);
}

/**
 * Lightweight parser for the grid list query (GetClientRecordsList). Only the
 * fields shown in the grid and used by the client-side filters are populated;
 * heavy detail fields (comments, documents, personnel) are loaded on demand by
 * the detail views instead.
 *
 * Engagement-record data (executed status, created/updated timestamps) is loaded
 * separately via GetEngagementRecordsList and merged in by client number so the
 * client list query stays a flat, fast table scan with no nested joins.
 */
// Fast YYYYMMDD (stored as an integer, e.g. 20230115) -> Dayjs. Avoids dayjs's
// customParseFormat string path, which is comparatively slow when run per row
// across the full ~140k-row dataset.
function parseYyyymmdd(value: number | null | undefined): Dayjs | null {
  if (value == null || value <= -1) return null;
  const year = Math.floor(value / 10000);
  const month = Math.floor(value / 100) % 100;
  const day = value % 100;
  if (!year || !month || !day) return null;
  return dayjs(new Date(year, month - 1, day));
}

function toParsedListRecord(
  record: EngagementListRecord,
  engagementByClient?: Map<string, EngagementDetailRecord>,
): ParsedEngagementRecord {
  const engagement = engagementByClient?.get(record.ClientNumber);
  const practiceGroup = record.ResponsibleAttorney?.PrimaryPracticeGroup?.SubSubPracticeGroupName || undefined;

  return {
    id: record.ClientNumber,
    engagementRecordId: engagement?.id,
    clientNumber: record.ClientNumber,
    clientName: record.ClientName1 || undefined,
    clientOpenDate: parseYyyymmdd(record.DateClientOpen == null ? null : Number(record.DateClientOpen)),
    clientClosedDate: parseYyyymmdd(record.DateClientClosed == null ? null : Number(record.DateClientClosed)),
    responsibleAttorney: record.ResponsibleAttorney
      ? { displayName: record.ResponsibleAttorney.NamePreferredFull || undefined, practiceGroup }
      : undefined,
    practiceGroup,
    isExecuted: Boolean(engagement?.engagement_imanage_documents?.length),
    type: '',
    iManageDocuments: [],
    iManageComments: [],
    createdAt: engagement?.created_at ? dayjs(engagement.created_at) : null,
    createdBy: '',
    updatedAt: engagement?.updated_at ? dayjs(engagement.updated_at) : null,
    updatedBy: '',
  };
}

export function parseEngagementListRecords(
  listData: EngagementListRecord[],
  engagementByClient?: Map<string, EngagementDetailRecord>,
): ParsedEngagementRecord[] {
  if (!listData) return [];
  return listData.map((record) => toParsedListRecord(record, engagementByClient));
}

/**
 * Chunked, non-blocking version of parseEngagementListRecords. Processes the
 * dataset in slices and yields to the event loop between slices so parsing the
 * full ~140k-row dataset does not freeze the UI. Pass `isCancelled` to abort
 * early (e.g. when the component unmounts).
 */
export async function parseEngagementListRecordsAsync(
  listData: EngagementListRecord[],
  engagementByClient?: Map<string, EngagementDetailRecord>,
  options?: { chunkSize?: number; isCancelled?: () => boolean },
): Promise<ParsedEngagementRecord[]> {
  if (!listData) return [];

  const chunkSize = options?.chunkSize ?? 4000;
  const isCancelled = options?.isCancelled;
  const result: ParsedEngagementRecord[] = new Array(listData.length);

  async function parseChunk(start: number): Promise<ParsedEngagementRecord[]> {
    if (start >= listData.length || isCancelled?.()) return result;

    const end = Math.min(start + chunkSize, listData.length);
    for (let i = start; i < end; i += 1) {
      result[i] = toParsedListRecord(listData[i], engagementByClient);
    }

    // Yield so the browser can paint / stay responsive between chunks.
    await new Promise((resolve) => {
      setTimeout(resolve);
    });

    return parseChunk(end);
  }

  return parseChunk(0);
}

export default useParseRecords;
